import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, PowerOff, Power, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Dialog, Input, ManagedDataTable } from '@/design-system';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { useCompanyDetails } from '../company-details-context';
import { companiesService, type CompanyIntegrationEntitlement } from '../api/companies.service';

const SOURCE_LABELS: Record<string, string> = {
  default: 'Padrão do catálogo',
  plan: 'Plano',
  staff: 'Suporte (Manager)',
  tenant: 'Cliente (App)',
  addon: 'Add-on',
};
const MIN_REASON = 5;
const fmtDateTime = (value: string | null | undefined) => (value ? new Date(value).toLocaleString('pt-BR') : '—');

export function CompanyDetailsIntegrationsTab() {
  const { company, staffUser } = useCompanyDetails();
  return (
    <CompanyIntegrations
      key={`${company.id}:${staffUser?.role ?? 'anonymous'}`}
      companyId={company.id}
      companyName={company.name}
      canEdit={['SUPERADMIN', 'SUPPORT'].includes(staffUser?.role ?? '')}
    />
  );
}

function CompanyIntegrations({ companyId, companyName, canEdit }: { companyId: string; companyName: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const onError = useErrorToast();
  const [target, setTarget] = useState<{ item: CompanyIntegrationEntitlement; enable: boolean } | null>(null);
  const [reason, setReason] = useState('');
  const query = useQuery({
    queryKey: queryKeys.companies.integrationEntitlements(companyId),
    queryFn: () => companiesService.listIntegrationEntitlements(companyId),
  });
  const items = query.data ?? [];
  const divergent = items.filter((item) => item.divergence);

  const change = useMutation({
    mutationFn: () => {
      if (!target || reason.trim().length < MIN_REASON) throw new Error('Informe o motivo da alteração.');
      return companiesService.updateIntegrationEntitlement(companyId, target.item.integrationId, { enabled: target.enable, reason: reason.trim() });
    },
    onSuccess: async (result) => {
      const name = target?.item.displayName ?? 'Integração';
      setTarget(null);
      setReason('');
      await qc.invalidateQueries({ queryKey: queryKeys.companies.integrationEntitlements(companyId) });
      toast.success(`${name} ${result.enabled ? 'habilitada' : 'desabilitada'}.${result.gatewaysAffected ? ` ${result.gatewaysAffected} gateway(s) ${result.enabled ? 'liberado(s)' : 'pausado(s)'}.` : ''}`);
    },
    onError: (error) => onError(error, 'Não foi possível alterar a integração'),
  });

  const columns: ColumnDef<CompanyIntegrationEntitlement>[] = [
    {
      id: 'integration', header: 'Integração', accessorFn: (item) => item.displayName,
      cell: ({ row }) => <div><p className="font-medium">{row.original.displayName}</p><p className="text-xs text-gray-500">{row.original.category}</p></div>,
    },
    {
      id: 'state', header: 'Estado', accessorFn: (item) => (item.effectiveEnabled ? 'Habilitada' : 'Desabilitada'),
      cell: ({ row }) => (
        <div>
          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${row.original.effectiveEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
            {row.original.effectiveEnabled ? 'Habilitada' : 'Desabilitada'}
          </span>
          {!row.original.entitlement && <p className="mt-1 text-xs text-gray-500">Sem registro: vale o padrão do catálogo</p>}
        </div>
      ),
    },
    {
      id: 'plan', header: 'Plano', accessorFn: (item) => (item.planAllows === null ? 'Não se aplica' : item.planAllows ? 'Libera' : 'Não libera'),
      cell: ({ row }) => (row.original.planAllows === null ? <span className="text-gray-400">—</span> : row.original.planAllows ? 'Libera' : 'Não libera'),
    },
    {
      id: 'source', header: 'Origem', accessorFn: (item) => (item.entitlement ? SOURCE_LABELS[item.entitlement.source] ?? item.entitlement.source : '—'),
    },
    {
      id: 'change', header: 'Última alteração', enableSorting: false,
      cell: ({ row }) => {
        const { entitlement, lastChange } = row.original;
        if (!entitlement) return <span className="text-gray-400">—</span>;
        return (
          <div className="text-sm">
            <p>{fmtDateTime(lastChange?.at ?? entitlement.updatedAt)}</p>
            {lastChange?.byEmail && <p className="text-xs text-gray-500">por {lastChange.byEmail} ({lastChange.byType === 'STAFF' ? 'staff' : 'cliente'})</p>}
            {lastChange?.reason && <p className="text-xs text-gray-500">Motivo: {lastChange.reason}</p>}
            {!lastChange && entitlement.notes && <p className="text-xs text-gray-500">{entitlement.notes}</p>}
          </div>
        );
      },
    },
    {
      id: 'gateways', header: 'Gateways', accessorFn: (item) => item.gateways.total,
      cell: ({ row }) => (row.original.gateways.total ? `${row.original.gateways.enabled} de ${row.original.gateways.total} ativos` : '—'),
    },
  ];
  if (canEdit) {
    columns.push({
      id: 'actions', header: 'Ações', enableSorting: false,
      cell: ({ row }) => row.original.effectiveEnabled
        ? <Button variant="ghost" size="sm" disabled={change.isPending} onClick={() => { setTarget({ item: row.original, enable: false }); setReason(''); }}><PowerOff size={14} />Desabilitar</Button>
        : <Button variant="subtle" size="sm" disabled={change.isPending} onClick={() => { setTarget({ item: row.original, enable: true }); setReason(''); }}><Power size={14} />Habilitar</Button>,
    });
  }

  const enabling = target?.enable === true;
  return (
    <div className="space-y-4">
      {divergent.length > 0 && (
        <div role="alert" className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">O plano libera a integração, mas ela está desabilitada para esta empresa.</p>
            <p>
              {divergent.map((item) => item.displayName).join(', ')}: o módulo aparece para o cliente, porém nenhuma ação funciona
              (resposta INTEGRATION_DISABLED). {canEdit ? 'Use "Habilitar" para liberar o uso.' : 'Peça a um perfil de Suporte ou Superadmin para habilitar.'}
            </p>
          </div>
        </div>
      )}
      <ManagedDataTable
        title="Integrações da empresa"
        subtitle="Estado da autorização de uso de cada integração. O plano define o que aparece; o entitlement define o que funciona."
        columns={columns} data={items} getRowId={(item) => item.integrationId} loading={query.isFetching}
        searchPlaceholder="Buscar integração…" emptyMessage="Nenhuma integração encontrada."
        toolbarButtons={[{ label: 'Atualizar', icon: <RefreshCw />, variant: 'subtle', disabled: query.isFetching || change.isPending, onClick: () => { void query.refetch(); } }]}
      />
      {query.isError && <p role="alert" className="text-sm text-red-600">Não foi possível carregar as integrações desta empresa.</p>}
      <Dialog
        isOpen={!!target}
        onClose={() => { if (!change.isPending) { setTarget(null); setReason(''); } }}
        title={enabling ? 'Habilitar integração' : 'Desabilitar integração'}
        footer={<>
          <Button variant="subtle" onClick={() => { setTarget(null); setReason(''); }} disabled={change.isPending}>Cancelar</Button>
          <Button variant={enabling ? 'primary' : 'danger'} onClick={() => change.mutate()} isLoading={change.isPending} disabled={reason.trim().length < MIN_REASON}>
            {enabling ? 'Confirmar: habilitar' : 'Confirmar: desabilitar'}
          </Button>
        </>}
      >
        {target && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {enabling
                ? `Habilitar "${target.item.displayName}" para "${companyName}"? O cliente volta a conseguir usar a integração e os gateways pareados dela são liberados.`
                : `Desabilitar "${target.item.displayName}" para "${companyName}"? O cliente deixa de conseguir usar a integração e os gateways pareados dela serão pausados.`}
            </p>
            <Input label="Motivo (obrigatório)" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Ex.: chamado 1234, cliente solicitou…" />
            <p className="text-xs text-gray-500">A alteração fica registrada com o seu e-mail, a empresa, a integração e este motivo.</p>
          </div>
        )}
      </Dialog>
    </div>
  );
}
